import { createCartClient } from '@theme/someone-atc-cart'
import { normalizeRules } from '@theme/someone-atc-config'
import { readGrants, writeGrants } from '@theme/someone-atc-grants'
import { grantedQuantities, planCartChanges } from '@theme/someone-atc-plan'
import { watchCartWrites } from '@theme/someone-atc-watch'

const TAG = 'someone-auto-add-to-cart'
const COALESCE_MS = 150
const THEME_EVENTS = ['cart:update', 'someone:atc:updated']

/**
 * @param {Window} scope
 * @returns {void}
 */
export function defineAutoAddToCart(scope) {
  if (scope.customElements.get(TAG) !== undefined) return

  class SomeoneAutoAddToCart extends scope.HTMLElement {
    #rules = []
    #client = null
    #watcher = null
    #queue = Promise.resolve()
    #timer = 0

    connectedCallback() {
      this.#rules = this.#readRules()
      if (this.#rules.length === 0) return
      this.#client = createCartClient(
        this.#rules[0].routes,
        scope.fetch.bind(scope)
      )
      this.#watcher = watchCartWrites(() => this.#coalesce(), scope)
      this.reconcile()
    }

    disconnectedCallback() {
      scope.clearTimeout(this.#timer)
      this.#watcher?.stop()
      this.#watcher = null
    }

    /**
     * @returns {Promise<void>}
     */
    reconcile() {
      this.#queue = this.#queue.then(() => this.#run()).catch(() => undefined)
      return this.#queue
    }

    #coalesce() {
      scope.clearTimeout(this.#timer)
      this.#timer = scope.setTimeout(() => this.reconcile(), COALESCE_MS)
    }

    #readRules() {
      const shared = this.querySelector('script[data-someone-atc-shared]')
      if (shared === null) return []
      try {
        const rules = [
          ...this.querySelectorAll('script[data-someone-atc-rule]')
        ].map((script) => JSON.parse(script.textContent))
        return normalizeRules({ ...JSON.parse(shared.textContent), rules })
      } catch {
        return []
      }
    }

    async #run() {
      let cart = await this.#client.read()
      let moved = false
      for (const rule of this.#rules) {
        const settled = await this.#runRule(rule, cart)
        if (settled === null) continue
        cart = settled
        moved = true
      }
      if (moved) this.#announce(cart)
    }

    /**
     * @param {object} rule
     * @param {object} cart
     * @returns {Promise<object|null>}
     */
    async #runRule(rule, cart) {
      const granted = readGrants(scope.sessionStorage, cart.token, rule.ruleId)
      const plan = planCartChanges(cart, rule, {
        now: Date.now(),
        roll: Math.random,
        granted
      })
      if (plan.add.length === 0 && plan.updates.length === 0) return null
      for (const update of plan.updates) {
        await this.#client.setQuantity(update.id, update.quantity)
      }
      await this.#client.add(plan.add)
      const settled = await this.#client.read()
      writeGrants(
        scope.sessionStorage,
        settled.token,
        rule.ruleId,
        grantedQuantities(settled, rule)
      )
      return settled
    }

    #announce(cart) {
      const detail = {
        cart,
        resource: cart,
        source: TAG,
        data: {
          source: TAG,
          itemCount: cart.item_count,
          changeType: 'add',
          preventAutoOpen: true
        }
      }
      for (const name of THEME_EVENTS) {
        scope.document.dispatchEvent(
          new scope.CustomEvent(name, { bubbles: true, detail })
        )
      }
    }
  }

  scope.customElements.define(TAG, SomeoneAutoAddToCart)
}

if (typeof window !== 'undefined' && window.customElements !== undefined) {
  defineAutoAddToCart(window)
}
